import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Package, FolderOpen, Plus, Truck, Paperclip } from 'lucide-react';
import { useOrder, useDeleteOrder } from '../hooks/useOrders.js';
import { useFulfillments } from '../hooks/useFulfillments.js';
import { toast } from '../lib/toast.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import OrderModal from '../components/orders/OrderModal.js';
import FulfillmentModal from '../components/fulfillments/FulfillmentModal.js';
import FulfillmentCard from '../components/fulfillments/FulfillmentCard.js';
import FileAttachments from '../components/files/FileAttachments.js';
import { useFeatureFlag } from '../lib/posthog.js';

const STATUS_COLORS: Record<string, string> = {
  draft: 'var(--color-text-muted)',
  in_production: 'var(--color-brand)',
  complete: 'var(--color-success)',
  cancelled: 'var(--color-danger)',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'var(--color-text-muted)',
  normal: 'var(--color-text-secondary)',
  high: 'var(--color-amber)',
  urgent: 'var(--color-danger)',
};

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const { t: tf } = useTranslation('fulfillments');
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const fulfillmentsEnabled = useFeatureFlag('fulfillments');
  const filesEnabled = useFeatureFlag('files');
  const [editOpen, setEditOpen] = useState(false);

  const [fulfillmentOpen, setFulfillmentOpen] = useState(false);

  const { data: order, isLoading, error } = useOrder(id ?? '');
  const { data: fulfillments = [] } = useFulfillments(id ?? '');
  const deleteOrder = useDeleteOrder();

  async function handleDelete() {
    if (!order) return;
    if (!confirm(t('detail.delete_confirm', { label: orderLabel }))) return;
    try {
      await deleteOrder.mutateAsync(order.id);
      toast.success(t('toast.deleted', { label: orderLabel }));
      navigate('/orders');
    } catch (_err) {
      toast.error(t('toast.delete_error', { label: orderLabel }));
    }
  }

  const orderLabel = workspace?.orderLabel ?? 'Order';
  const currency = workspace?.currency ?? 'USD';

  if (isLoading) {
    return (
      <div className="page-enter">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="skeleton-line" style={{ width: 200, height: 24 }} />
          <div className="skeleton-block" style={{ height: 160, borderRadius: 'var(--radius-lg)' }} />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="page-enter" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
        <Package size={48} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-4)' }} />
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          {tc('errors.not_found')}
        </p>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[order.status] ?? 'var(--color-text-secondary)';
  const priorityColor = PRIORITY_COLORS[order.priority] ?? 'var(--color-text-secondary)';

  return (
    <div className="page-enter">
      {/* Back + header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <button
          onClick={() => navigate(-1)}
          aria-label={tc('actions.back')}
          style={{
            background: 'none', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            color: 'var(--color-text-secondary)', display: 'flex',
            alignItems: 'center', gap: 'var(--space-1)',
            padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-sm)',
          }}
        >
          <ArrowLeft size={14} /> {tc('actions.back')}
        </button>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono, monospace)', marginBottom: 2 }}>
            {order.orderNumber}
          </p>
          <h1 className="page-title">{order.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={() => setEditOpen(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              color: 'var(--color-text)', background: 'var(--color-bg)', cursor: 'pointer',
            }}
          >
            {t('detail.edit_button')}
          </button>
          <button
            onClick={handleDelete}
            style={{
              padding: 'var(--space-2) var(--space-4)', border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              color: 'var(--color-danger)', background: 'var(--color-bg)', cursor: 'pointer',
            }}
          >
            {t('detail.delete_button')}
          </button>
        </div>
      </div>

      {/* Summary card */}
      <div style={{
        background: 'var(--color-bg)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)',
        marginBottom: 'var(--space-6)',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: 'var(--space-5)',
      }}>
        {/* Status */}
        <div>
          <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.status')}</p>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: statusColor,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
            {t(`status.${order.status}`)}
          </span>
        </div>

        {/* Stage */}
        {order.stageName && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.stage')}</p>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: order.stageColor ?? 'var(--color-gray-400)', flexShrink: 0 }} />
              {order.stageName}
            </span>
          </div>
        )}

        {/* Priority */}
        <div>
          <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.priority')}</p>
          <p style={{ fontSize: 'var(--text-sm)', color: priorityColor, fontWeight: order.priority === 'urgent' ? 'var(--font-medium)' : undefined }}>
            {t(`priority.${order.priority}`)}
          </p>
        </div>

        {/* Due date */}
        {order.dueDate && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.due_date')}</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{order.dueDate}</p>
          </div>
        )}

        {/* Project link */}
        <div>
          <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.bucket', { bucket_label: workspace?.bucketLabel ?? 'Project' })}</p>
          <button
            onClick={() => navigate(`/projects/${order.bucketId}`)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: 'var(--text-sm)', color: 'var(--color-brand)',
              display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            }}
          >
            <FolderOpen size={13} />
            {workspace?.bucketLabel ?? 'Project'}
          </button>
        </div>

        {/* Total */}
        {order.total !== '0' && (
          <div>
            <p className="label" style={{ marginBottom: 'var(--space-2)' }}>{t('columns.total')}</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', fontWeight: 'var(--font-medium)' }}>
              {currency} {parseFloat(order.total).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {/* Line items */}
      {(order.lineItems?.length ?? 0) > 0 && (
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
          marginBottom: 'var(--space-5)',
        }}>
          <p className="label" style={{ marginBottom: 'var(--space-4)' }}>{t('detail.line_items_section')}</p>

          {/* Line item table header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 80px 100px 80px 100px',
            gap: 'var(--space-4)', paddingBottom: 'var(--space-2)',
            borderBottom: '1px solid var(--color-border)', marginBottom: 'var(--space-2)',
          }}>
            {[t('form.description_label'), t('form.quantity_label'), t('form.unit_price_label'), t('form.press_label'), t('form.line_total')].map((h) => (
              <span key={h} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-medium)' }}>{h}</span>
            ))}
          </div>

          {order.lineItems!.map((item) => (
            <div key={item.id} style={{
              display: 'grid', gridTemplateColumns: '2fr 80px 100px 80px 100px',
              gap: 'var(--space-4)', padding: 'var(--space-2) 0',
              borderBottom: '1px solid var(--color-gray-100)', alignItems: 'center',
            }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{item.description}</span>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{item.quantity}</span>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{item.unitPrice}</span>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{item.press ?? '—'}</span>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{item.total}</span>
            </div>
          ))}

          {/* Totals row */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--space-1)', marginTop: 'var(--space-3)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('form.subtotal_label')}</span>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', minWidth: 80, textAlign: 'right' }}>
                {currency} {parseFloat(order.subtotal).toLocaleString()}
              </span>
            </div>
            {order.tax !== '0' && (
              <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('form.tax_row_label')}</span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', minWidth: 80, textAlign: 'right' }}>
                  {currency} {parseFloat(order.tax).toLocaleString()}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-8)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                {t('form.order_total_label')}
              </span>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', minWidth: 80, textAlign: 'right' }}>
                {currency} {parseFloat(order.total).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {order.notes && (
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
        }}>
          <p className="label" style={{ marginBottom: 'var(--space-3)' }}>{t('form.notes_label')}</p>
          <p style={{
            fontSize: 'var(--text-sm)', color: 'var(--color-text)',
            lineHeight: 'var(--leading-relaxed)', whiteSpace: 'pre-wrap',
          }}>
            {order.notes}
          </p>
        </div>
      )}

      {/* Fulfillments */}
      {fulfillmentsEnabled && <div style={{
        background: 'var(--color-bg)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
        marginTop: 'var(--space-5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Truck size={16} style={{ color: 'var(--color-text-muted)' }} />
            <p className="label">{tf('title')}</p>
          </div>
          <button
            onClick={() => setFulfillmentOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
              padding: '3px var(--space-3)', background: 'none',
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', cursor: 'pointer',
            }}
          >
            <Plus size={12} /> {tf('add')}
          </button>
        </div>

        {fulfillments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8) var(--space-4)' }}>
            <Truck size={32} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-3)' }} />
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{tf('none')}</p>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>{tf('none_sub')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {fulfillments.map((f) => (
              <FulfillmentCard key={f.id} fulfillment={f} orderId={id ?? ''} />
            ))}
          </div>
        )}
      </div>}

      {/* Files */}
      {filesEnabled && id && (
        <div style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
          marginTop: 'var(--space-5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
            <Paperclip size={16} style={{ color: 'var(--color-text-muted)' }} />
            <p className="label">{t('files:tab', 'Files')}</p>
          </div>
          <FileAttachments entityType="order" entityId={id} />
        </div>
      )}

      {editOpen && (
        <OrderModal
          order={order}
          onClose={() => setEditOpen(false)}
        />
      )}

      {fulfillmentOpen && (
        <FulfillmentModal orderId={id ?? ''} onClose={() => setFulfillmentOpen(false)} />
      )}
    </div>
  );
}
